import React, { useEffect, useState } from "react";
import { MapTrifold, ArrowRight, Spinner } from "@phosphor-icons/react";
import api from "@/lib/api";

/**
 * Compact metro-density widget for the Discover page.
 * Shows the top cities the community (or this user) has already discovered,
 * ranked by number of cached businesses. Clicking a metro auto-fills the
 * parent form via `onPick(city, state, industry)`.
 */
export default function CoverageMap({ onPick }) {
  const [loading, setLoading] = useState(true);
  const [metros, setMetros] = useState([]);
  const [stats, setStats] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/discover/coverage")
      .then((r) => {
        if (cancelled) return;
        setMetros(r.data.metros || []);
        setStats(r.data.stats || null);
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div
        data-testid="coverage-map-loading"
        className="bg-zinc-900/60 border border-zinc-800 rounded-sm p-5 flex items-center gap-3"
      >
        <Spinner size={14} className="animate-spin text-orange-500" />
        <div className="text-xs font-mono text-zinc-500">Loading coverage map…</div>
      </div>
    );
  }

  if (!metros.length) {
    return (
      <div
        data-testid="coverage-map-empty"
        className="bg-zinc-900/60 border border-zinc-800 rounded-sm p-5"
      >
        <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-zinc-500">
          <MapTrifold size={14} weight="fill" className="text-orange-500" />
          Coverage map
        </div>
        <div className="mt-2 text-sm text-zinc-400">
          No metros discovered yet. Run your first search above — this map will
          fill in as you (and the network) explore new cities.
        </div>
      </div>
    );
  }

  const visible = expanded ? metros : metros.slice(0, 6);

  return (
    <div
      data-testid="coverage-map"
      className="bg-zinc-900/60 border border-zinc-800 rounded-sm p-5"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <MapTrifold size={14} weight="fill" className="text-orange-500" />
          <div className="text-xs font-mono uppercase tracking-wider text-zinc-300">
            High-density metros
          </div>
        </div>
        {stats && (
          <div
            className="text-[10px] font-mono text-zinc-500"
            data-testid="coverage-stats"
          >
            {stats.total_cities} cities · {stats.total_elements.toLocaleString()} businesses cached
          </div>
        )}
      </div>
      <div className="text-[11px] font-mono text-zinc-600 mt-1 mb-3">
        Click a metro to prospect it next. Sorted by discovered businesses.
      </div>

      <div className="space-y-1.5" data-testid="coverage-metro-list">
        {visible.map((m, idx) => {
          const top = m.top_industries.slice(0, 2).join(" · ") || "—";
          return (
            <button
              key={`${m.city}-${m.state}`}
              type="button"
              data-testid={`coverage-metro-${m.city.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() =>
                onPick?.(m.city, m.state, m.top_industries[0] || null)
              }
              className="w-full flex items-center justify-between gap-3 px-3 py-2 bg-zinc-950/60 border border-zinc-800 hover:border-orange-500/50 hover:bg-zinc-900 rounded-sm transition-colors text-left group"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="text-[10px] font-mono text-zinc-600 tabular-nums w-4 flex-shrink-0">
                  {idx + 1}
                </div>
                <div className="min-w-0">
                  <div className="text-sm text-zinc-100 truncate">
                    {m.city}
                    <span className="text-zinc-500 font-mono text-[11px] ml-1.5">
                      {m.state}
                    </span>
                  </div>
                  <div className="text-[10px] font-mono text-zinc-500 truncate">
                    Top: {top}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <div className="text-xs font-mono text-orange-400 tabular-nums">
                  {m.total_elements.toLocaleString()}
                </div>
                <ArrowRight
                  size={12}
                  className="text-zinc-600 group-hover:text-orange-400 transition-colors"
                />
              </div>
            </button>
          );
        })}
      </div>

      {metros.length > 6 && (
        <button
          type="button"
          data-testid="coverage-toggle-expand"
          onClick={() => setExpanded((v) => !v)}
          className="mt-3 w-full text-[11px] font-mono text-zinc-500 hover:text-orange-400 transition-colors"
        >
          {expanded ? "Show top 6" : `Show all ${metros.length} metros`}
        </button>
      )}
    </div>
  );
}
